// The live half of the Loremaster's Compass character lane, the wiring.
// Renders nothing. While the connector announces the lane
// (DM_SCREEN_LMC_LIVE_READY), every change to a sheet character's live state
// is sent to Compass (debounced, only when the fingerprint moved, the whole
// roster once on connect); a Compass edit arriving as DM_SCREEN_LMC_LIVE_APPLY
// is applied to the matching character and written to the activity log.
// Neither side echoes: an equal value is not a change on either end.
import { useEffect, useRef } from "react";
import { newRequestId, onBridge, post } from "../bridge";
import { applyLive, describeLive, liveFingerprint, liveSnapshot, sheetIdOf, type LiveApply } from "../state/compassLive";
import { useStore } from "../state/store";
import { campaignRoster } from "../state/campaign";

const PUSH_DEBOUNCE_MS = 800;

export function CompassLive() {
  const { layout, update, logActivity } = useStore();
  const ready = useRef(false);
  const lastSent = useRef("");
  const timer = useRef<number | null>(null);
  // The live lane carries the table in play. Pushing every sheet the console has
  // read would hand Compass another campaign's party as though it were this one.
  const roster = campaignRoster(layout);
  const latest = useRef(roster);
  latest.current = roster;

  // what the page sends: the roster's live state, when it moved
  const push = (force = false) => {
    const states = liveSnapshot(latest.current);
    const fp = liveFingerprint(states);
    if (!force && fp === lastSent.current) return;
    if (!states.length) { lastSent.current = fp; return; }
    lastSent.current = fp;
    post({ type: "DM_SCREEN_LMC_LIVE_PUSH", requestId: newRequestId("lmc-live"), characters: states });
  };

  useEffect(() => {
    const off = onBridge((m) => {
      if (m.type === "DM_SCREEN_LMC_LIVE_READY") {
        const was = ready.current; ready.current = true;
        if (!was || m.port) push(true);   // the whole roster on connect, so Compass starts from the truth
        return;
      }
      if (m.type === "DM_SCREEN_LMC_LIVE_GONE") { ready.current = false; return; }
      if (m.type !== "DM_SCREEN_LMC_LIVE_APPLY" || !Array.isArray(m.items)) return;
      const items = m.items as LiveApply[];
      const notes: string[] = [];
      update((l) => {
        let changed = false;
        const characters = l.characters.map((c) => {
          const id = sheetIdOf(c); if (!id) return c;
          const item = items.filter((it) => it && String(it.characterId) === id).pop(); if (!item || !item.fields) return c;
          const next = applyLive(c, item.fields);
          if (next !== c) { changed = true; const what = describeLive(c, next); if (what) notes.push(`${c.name}: ${what}`); }
          return next;
        });
        return changed ? { ...l, characters } : l;
      });
      notes.forEach((text) => logActivity({ text, meta: "from Loremaster's Compass", tone: "compass" }));
    });
    return off;
  }, [update, logActivity]);

  // every roster change, debounced, while the lane is up
  useEffect(() => {
    if (!ready.current) return;
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => { timer.current = null; push(); }, PUSH_DEBOUNCE_MS);
    return () => { if (timer.current) { window.clearTimeout(timer.current); timer.current = null; } };
  }, [layout.characters, layout.activeCampaign, layout.campaignSnapshots]);

  return null;
}
